import React, { useId } from "react";
import { Skeleton } from "@mui/material";
import { Box } from "../../primitives/Box/Box";
import { FlexColumn } from "../Layout/FlexColumn/FlexColumn";
import { FlexRow } from "../Layout/FlexRow/FlexRow";
import { StyledImage } from "../Image/Image";
import { Icon } from "../Icon/Icon";
import {
  Body3RegularTypography,
  Body5RegularTypography,
  Caps2SemiboldTypography,
} from "../../primitives/Typography/typographyVariants";
import { colorPalette } from "../../theme/colors";
import type {
  DiscoverSearchMatchBrand,
  DiscoverSearchPanelProps,
  DiscoverSearchPopularBrand,
  DiscoverSearchRecentItem,
  DiscoverSearchTextSuggestion,
} from "./DiscoverSearchPanel.types";

const DEFAULT_MAX_HEIGHT = "min(420px, 72vh)";
const ICON_COLOR = colorPalette.gray[600];
const TILE_SIZE = 36;
const SKELETON_ROW_COUNT = 2;

function preventBlurMouseDown(e: React.MouseEvent) {
  e.preventDefault();
}

type SectionTitleProps = {
  id: string;
  children: React.ReactNode;
};

const SectionTitle: React.FC<SectionTitleProps> = ({ id, children }) => (
  <Caps2SemiboldTypography
    id={id}
    color={colorPalette.gray[600]}
    px="16px"
    pb="8px"
    display="block"
    width="100%"
    textAlign="left"
  >
    {children}
  </Caps2SemiboldTypography>
);

const HistoryIconTile: React.FC = () => (
  <FlexRow
    width={TILE_SIZE}
    height={TILE_SIZE}
    borderRadius={10}
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    backgroundColor={colorPalette.gray[300]}
    aria-hidden
  >
    <Icon
      name="fa-regular fa-clock"
      color={ICON_COLOR}
      height="16"
      width="16"
      size="14"
    />
  </FlexRow>
);

const SearchIconTile: React.FC = () => (
  <FlexRow
    width={TILE_SIZE}
    height={TILE_SIZE}
    borderRadius={10}
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    backgroundColor={colorPalette.gray[300]}
    aria-hidden
  >
    <Icon
      name="fa-solid fa-magnifying-glass"
      color={ICON_COLOR}
      height="14"
      width="14"
      size="14"
    />
  </FlexRow>
);

type LogoTileProps = {
  src?: string | null;
  circular?: boolean;
};

const LogoTile: React.FC<LogoTileProps> = ({ src, circular = false }) => (
  <FlexRow
    width={TILE_SIZE}
    height={TILE_SIZE}
    borderRadius={circular ? "50%" : 10}
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    overflow="hidden"
    backgroundColor={colorPalette.gray[300]}
    aria-hidden
  >
    {src ? (
      <StyledImage
        src={src}
        alt=""
        width={circular ? "36" : "32"}
        height={circular ? "36" : "32"}
        objectFit={circular ? "cover" : "contain"}
        borderRadius={circular ? "50%" : "8px"}
      />
    ) : null}
  </FlexRow>
);

type ListRowButtonProps = {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  paddingY: string;
  paddingX?: string;
};

const ListRowButton: React.FC<ListRowButtonProps> = ({
  onClick,
  icon,
  label,
  paddingY,
  paddingX = "16px",
}) => (
  <Box
    as="button"
    type="button"
    width="100%"
    minHeight="48px"
    border="none"
    backgroundColor="transparent"
    cursor="pointer"
    textAlign="left"
    py={paddingY}
    px={paddingX}
    onMouseDown={preventBlurMouseDown}
    onClick={onClick}
  >
    <FlexRow gap="12px" alignItems="center" width="100%" minWidth={0}>
      {icon}
      <Body3RegularTypography
        color={colorPalette.gray[900]}
        lineClamp={1}
        flex={1}
        minWidth={0}
      >
        {label}
      </Body3RegularTypography>
    </FlexRow>
  </Box>
);

const SuggestionSkeletonRows: React.FC = () => (
  <Box px="16px" py="8px">
    {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
      <Skeleton
        key={index}
        height={48}
        style={index > 0 ? { marginTop: 4 } : undefined}
      />
    ))}
  </Box>
);

type PanelSectionProps = {
  titleId: string;
  title: string;
  children: React.ReactNode;
  paddingTop?: string;
  paddingBottom?: string;
};

const PanelSection: React.FC<PanelSectionProps> = ({
  titleId,
  title,
  children,
  paddingTop = "8px",
  paddingBottom = "12px",
}) => (
  <Box
    as="section"
    aria-labelledby={titleId}
    width="100%"
    pt={paddingTop}
    pb={paddingBottom}
  >
    <SectionTitle id={titleId}>{title}</SectionTitle>
    {children}
  </Box>
);

export const DiscoverSearchPanel: React.FC<DiscoverSearchPanelProps> = ({
  open,
  query,
  recentItems = [],
  matchBrands = [],
  textSuggestions = [],
  popularBrands = [],
  loading = false,
  textLoading = false,
  onRecentSelect,
  onMatchSelect,
  onTextSuggestionSelect,
  onPopularSelect,
  pastSearchesTitle = "Past searches",
  popularSearchesTitle = "Popular searches",
  textSuggestionsTitle = "Suggestions",
  matchingBrandsTitle = "Matching brands",
  emptyRecentMessage = "No recent searches yet.",
  maxHeight = DEFAULT_MAX_HEIGHT,
  showBrands = true,
  className,
  "data-cy": dataCy,
}) => {
  const pastHeadingId = useId();
  const popularHeadingId = useId();
  const textHeadingId = useId();
  const matchHeadingId = useId();

  if (!open) return null;

  const trimmedQuery = query.trim();
  const hasQuery = trimmedQuery.length > 0;
  const showTextSuggestions = hasQuery && textSuggestions.length > 0;
  const showTextSkeleton = hasQuery && textLoading && !showTextSuggestions;
  const showBrandSuggestions = showBrands && hasQuery && matchBrands.length > 0;
  const showBrandSkeleton =
    showBrands && hasQuery && loading && !showBrandSuggestions;

  return (
    <Box
      role="region"
      aria-label="Brand search"
      className={className}
      data-cy={dataCy}
      position="absolute"
      top="calc(100% + 8px)"
      left={0}
      zIndex={80}
      width="100%"
      minWidth={0}
      backgroundColor={colorPalette.white[500]}
      border={`1px solid ${colorPalette.slate[200]}`}
      borderRadius={12}
      overflowX="hidden"
      overflowY="auto"
      style={{
        boxSizing: "border-box",
        maxHeight,
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {!hasQuery ? (
        showBrands ? (
          <FlexColumn width="100%" minWidth={0} alignItems="stretch">
            <PanelSection titleId={pastHeadingId} title={pastSearchesTitle}>
              {recentItems.length > 0 ? (
                <FlexColumn role="list" width="100%">
                  {recentItems.map((item: DiscoverSearchRecentItem) => (
                    <Box key={item.id} role="listitem">
                      <ListRowButton
                        label={item.display_name}
                        icon={<HistoryIconTile />}
                        paddingY="12px"
                        onClick={() => onRecentSelect?.(item)}
                      />
                    </Box>
                  ))}
                </FlexColumn>
              ) : (
                <Body5RegularTypography
                  color={colorPalette.gray[600]}
                  lineHeight={20}
                  mx="16px"
                  mb="4px"
                >
                  {emptyRecentMessage}
                </Body5RegularTypography>
              )}
            </PanelSection>

            {popularBrands.length > 0 ? (
              <PanelSection
                titleId={popularHeadingId}
                title={popularSearchesTitle}
                paddingTop="12px"
              >
                <FlexColumn role="list" width="100%">
                  {popularBrands.map((brand: DiscoverSearchPopularBrand) => (
                    <Box key={brand.id} role="listitem">
                      <ListRowButton
                        label={brand.display_name}
                        icon={<LogoTile src={brand.logo_url} />}
                        paddingY="12px"
                        onClick={() => onPopularSelect?.(brand)}
                      />
                    </Box>
                  ))}
                </FlexColumn>
              </PanelSection>
            ) : null}
          </FlexColumn>
        ) : null
      ) : (
        <FlexColumn width="100%" minWidth={0} alignItems="stretch">
          {showTextSkeleton ? (
            <PanelSection titleId={textHeadingId} title={textSuggestionsTitle}>
              <SuggestionSkeletonRows />
            </PanelSection>
          ) : null}

          {showTextSuggestions ? (
            <PanelSection titleId={textHeadingId} title={textSuggestionsTitle}>
              <FlexColumn role="list" width="100%">
                {textSuggestions.map(
                  (suggestion: DiscoverSearchTextSuggestion) => (
                    <Box key={suggestion.title} role="listitem">
                      <ListRowButton
                        label={suggestion.title}
                        icon={<SearchIconTile />}
                        paddingY="10px"
                        onClick={() => onTextSuggestionSelect?.(suggestion)}
                      />
                    </Box>
                  )
                )}
              </FlexColumn>
            </PanelSection>
          ) : null}

          {showBrandSkeleton ? (
            <PanelSection titleId={matchHeadingId} title={matchingBrandsTitle}>
              <SuggestionSkeletonRows />
            </PanelSection>
          ) : null}

          {showBrandSuggestions ? (
            <PanelSection titleId={matchHeadingId} title={matchingBrandsTitle}>
              <FlexColumn role="list" width="100%">
                {matchBrands.map((brand: DiscoverSearchMatchBrand) => (
                  <Box key={brand.id} role="listitem">
                    <ListRowButton
                      label={brand.display_name}
                      icon={<LogoTile src={brand.logo_url} circular />}
                      paddingY="10px"
                      onClick={() => onMatchSelect?.(brand)}
                    />
                  </Box>
                ))}
              </FlexColumn>
            </PanelSection>
          ) : null}

          {!showTextSkeleton &&
          !showTextSuggestions &&
          showBrands &&
          !showBrandSkeleton &&
          !showBrandSuggestions ? (
            <PanelSection titleId={matchHeadingId} title={matchingBrandsTitle}>
              <Body5RegularTypography
                color={colorPalette.gray[600]}
                lineHeight={20}
                mx="16px"
                mb="12px"
              >
                No brands match &ldquo;{trimmedQuery}&rdquo;.
              </Body5RegularTypography>
            </PanelSection>
          ) : null}
        </FlexColumn>
      )}
    </Box>
  );
};

export default DiscoverSearchPanel;
